import { c as _c } from "react/compiler-runtime";
import { z } from '@repo/schema';
import { GetUserSchema } from '@repo/schema/user';
import { formatUserName } from '@repo/sdk/helpers';
import { Avatar, AvatarFallback, AvatarImage } from '@repo/ui/components';
import { CircleUserRoundIcon } from '@repo/ui/icons';
import { cn, cva, type VariantProps } from '@repo/ui/lib/utils';
import React, { type ComponentProps, type CSSProperties } from 'react';
const schema = GetUserSchema.extend({
  email: z.string().nullish()
});
const avatarRootVariants = cva('', {
  defaultVariants: {
    variant: 'rounded'
  },
  variants: {
    variant: {
      rounded: 'rounded-full after:rounded-full',
      square: 'rounded-lg after:rounded-lg'
    }
  }
});
const avatarSurfaceVariants = cva('', {
  defaultVariants: {
    variant: 'rounded'
  },
  variants: {
    variant: {
      rounded: 'rounded-full',
      square: 'rounded-lg'
    }
  }
});
const avatarTextVariants = cva('p-2 font-semibold uppercase', {
  defaultVariants: {
    variant: 'rounded'
  },
  variants: {
    variant: {
      rounded: 'rounded-full',
      square: 'rounded-md'
    }
  }
});
interface IProps {
  alt?: string;
  className?: string;
  size?: ComponentProps<typeof Avatar>['size'];
  style?: CSSProperties;
  textClassName?: string;
  user?: null | z.infer<typeof schema>;
}
type KitAvatarProps = IProps & VariantProps<typeof avatarRootVariants>;
export const KitAvatar = t0 => {
  const $ = _c(30);
  const {
    alt,
    className,
    size,
    style,
    textClassName,
    user,
    variant
  } = t0;
  const legacySizeClassName = size ? undefined : "size-10";
  let t1;
  if ($[0] !== className || $[1] !== legacySizeClassName || $[2] !== variant) {
    t1 = cn(avatarRootVariants({
      variant
    }), legacySizeClassName, className);
    $[0] = className;
    $[1] = legacySizeClassName;
    $[2] = variant;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== alt || $[5] !== user) {
    t2 = alt ?? formatUserName(user, "");
    $[4] = alt;
    $[5] = user;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== variant) {
    t3 = avatarSurfaceVariants({
      variant
    });
    $[7] = variant;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = user?.avatar?.url;
  let t5;
  if ($[9] !== t2 || $[10] !== t3 || $[11] !== t4) {
    t5 = <AvatarImage alt={t2} className={t3} src={t4} data-sentry-element="AvatarImage" data-sentry-source-file="avatar.tsx" />;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== variant) {
    t6 = avatarSurfaceVariants({
      variant
    });
    $[13] = variant;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== className || $[16] !== size || $[17] !== textClassName || $[18] !== user || $[19] !== variant) {
    t7 = user?.name ? <div className={cn(avatarTextVariants({
      variant
    }), size === "sm" && "p-0 text-xs", textClassName)}>{user.name.charAt(0)}{user.last_name && user.last_name.charAt(0)}</div> : <CircleUserRoundIcon className={cn("size-full", avatarSurfaceVariants({
      variant
    }), className)} />;
    $[15] = className;
    $[16] = size;
    $[17] = textClassName;
    $[18] = user;
    $[19] = variant;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t6 || $[22] !== t7) {
    t8 = <AvatarFallback className={t6} data-sentry-element="AvatarFallback" data-sentry-source-file="avatar.tsx">{t7}</AvatarFallback>;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== size || $[25] !== style || $[26] !== t1 || $[27] !== t5 || $[28] !== t8) {
    t9 = <Avatar className={t1} size={size} style={style} data-sentry-element="Avatar" data-sentry-component="KitAvatar" data-sentry-source-file="avatar.tsx">{t5}{t8}</Avatar>;
    $[24] = size;
    $[25] = style;
    $[26] = t1;
    $[27] = t5;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
};